import type { CatalogProps } from '../index';
import { BuyLink, ProductGrid, Quote, TplFooter, TplRoot, money, splitProducts } from '../shared';
import { logoSrc } from '../sections';

/** Obsidian Luxury — serifada sobre ônix, toques champagne, atmosfera de gala. Jóias / premium. */
export default function Catalog({ data, apiBase }: CatalogProps) {
  const { store, products } = data;
  const { feature, grid } = splitProducts(store, products);
  const logo = logoSrc(store.logoUrl, apiBase);

  return (
    <TplRoot font="serif">
      <div className="border-b border-[#f59e0b]/20 bg-black/40 text-center py-2 text-[11px] tracking-[0.25em] uppercase text-[#f59e0b]/80">
        Peças numeradas · Certificado incluso
      </div>

      <header className="max-w-5xl mx-auto px-4 py-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          {logo ? <img src={logo} alt="" className="h-8 w-auto object-contain" /> : null}
          <span className="text-xl tracking-[0.2em] uppercase font-semibold">Maison {store.name}</span>
        </a>
        <nav className="hidden md:flex gap-8 text-sm tracking-widest uppercase text-white/50">
          <span>Coleção</span>
          <span>Atelier</span>
          <span>Concierge</span>
        </nav>
      </header>

      <section className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-sm aspect-[4/5] bg-gradient-to-br from-[#f59e0b]/15 via-white/[0.05] to-transparent border border-white/10 flex items-center justify-center">
          <span className="text-white/10 text-8xl">{store.name.charAt(0)}</span>
        </div>
        <div className="flex flex-col gap-5">
          <span className="text-[11px] tracking-[0.3em] uppercase text-[#f59e0b]">Haute Joaillerie · Coleção {new Date().getFullYear()}</span>
          <h1 className="text-4xl md:text-5xl leading-[1.1] font-semibold">
            {store.tagline ?? 'A nobreza do raro, esculpida sob medida.'}
          </h1>
          {feature ? (
            <>
              <p className="text-white/60 leading-relaxed [font-family:'Plus_Jakarta_Sans',sans-serif]">
                {feature.description ?? `${feature.name} — peça exclusiva da coleção.`}
              </p>
              <div className="flex items-baseline gap-4">
                <span className="text-2xl">{money(feature.amount)}</span>
              </div>
              <BuyLink href={`/p/${feature.slug}`} className="[font-family:'Plus_Jakarta_Sans',sans-serif] self-start">
                Adquirir peça exclusiva
              </BuyLink>
            </>
          ) : null}
        </div>
      </section>

      {/* trust row */}
      <section className="max-w-5xl mx-auto px-4 grid grid-cols-1 sm:grid-cols-3 gap-4 [font-family:'Plus_Jakarta_Sans',sans-serif]">
        {[
          ['verified', 'Certificado incluso', 'Cada peça acompanha dossiê de autenticação.'],
          ['diamond', 'Garantia de fábrica', 'Conforme política de troca e devolução da loja.'],
          ['local_shipping', 'Envio cuidadoso', 'Embalagem protegida para itens de valor.'],
        ].map(([icon, t, d]) => (
          <div key={t} className="rounded-sm border border-white/10 bg-white/[0.03] p-5 flex flex-col gap-2">
            <span className="material-symbols-outlined text-[#f59e0b] text-2xl">{icon}</span>
            <span className="font-semibold [font-family:'Playfair_Display',serif] text-lg">{t}</span>
            <span className="text-sm text-white/50">{d}</span>
          </div>
        ))}
      </section>

      <main className="max-w-5xl mx-auto px-4 py-12 flex flex-col gap-10">
        {grid.length ? (
          <div>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#f59e0b]">Curadoria exclusiva</span>
            <h2 className="text-3xl font-semibold mt-1 mb-6">Peças complementares</h2>
            <ProductGrid products={grid} cols={3} cardCta="Ver peça" />
          </div>
        ) : null}
        <Quote store={store} />
      </main>

      <TplFooter store={store} />
    </TplRoot>
  );
}
